/** Audit Log page (UX review L2/L3) — who changed what, when.
 *
 * Admin-only viewer over the immutable audit trail (auth events, balance
 * transfers, rate imports, account changes). Filters by action prefix,
 * actor, status, and date range; row click opens the full entry with
 * before/after state JSON.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { auditApi } from "../api/client";
import PageHeader from "../components/common/PageHeader";
import { useNotify } from "../components/common/NotificationProvider";
import { formatDateTime } from "../utils/format";

interface AuditEntry {
  id: string;
  created_at: string | null;
  actor_type: string;
  actor_id: string | null;
  actor_username: string | null;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  status: string;
  error_message: string | null;
  source_ip: string | null;
  user_agent: string | null;
  detail: unknown;
  before_state: unknown;
  after_state: unknown;
}

const PAGE_SIZE = 25;

/** Status chip for an audit entry. */
function StatusChip({ status }: { status: string }) {
  const color =
    status === "success"
      ? "success"
      : status === "failure"
      ? "error"
      : "default";
  return <Chip label={status} size="small" color={color} variant="outlined" />;
}

/** Pretty-print JSON state blocks. */
function StateBlock({ label, value }: { label: string; value: unknown }) {
  if (value === null || value === undefined) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>
      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "action.hover" }}>
        <Typography
          component="pre"
          sx={{
            fontFamily: "monospace",
            fontSize: "0.75rem",
            m: 0,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {typeof value === "string" ? value : JSON.stringify(value, null, 2)}
        </Typography>
      </Paper>
    </Box>
  );
}

/** Audit log page component. */
export default function AuditLog() {
  const notify = useNotify();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);

  // Filters
  const [actionFilter, setActionFilter] = useState("");
  const [actorTypeFilter, setActorTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Detail dialog
  const [selected, setSelected] = useState<AuditEntry | null>(null);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = {
        skip: page * PAGE_SIZE,
        limit: PAGE_SIZE,
      };
      if (actionFilter) params.action = actionFilter;
      if (actorTypeFilter) params.actor_type = actorTypeFilter;
      if (statusFilter) params.status = statusFilter;

      const response = await auditApi.list(params);
      setEntries(response.data.logs || []);
      setTotal(response.data.total ?? 0);
    } catch (err: any) {
      notify(err.response?.data?.detail || "Failed to load audit log", "error");
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter, actorTypeFilter, statusFilter, notify]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  return (
    <Box>
      <PageHeader
        title="Audit Log"
        subtitle="Immutable trail of security-relevant and money-path actions"
      />

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              label="Action prefix"
              placeholder="e.g. balance. or auth."
              value={actionFilter}
              onChange={(e) => {
                setActionFilter(e.target.value);
                setPage(0);
              }}
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>Actor</InputLabel>
              <Select
                value={actorTypeFilter}
                label="Actor"
                onChange={(e) => {
                  setActorTypeFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="reseller">Reseller</MenuItem>
                <MenuItem value="system">System</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="failure">Failure</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: 640 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>Actor</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Entity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Source IP</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading &&
                entries.length === 0 &&
                Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }).map((_, c) => (
                      <TableCell key={c}>…</TableCell>
                    ))}
                  </TableRow>
                ))}
              {!loading && entries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No audit entries match the current filters
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {entries.map((entry) => (
                <TableRow
                  key={entry.id}
                  hover
                  onClick={() => setSelected(entry)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>{formatDateTime(entry.created_at)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {entry.actor_username || entry.actor_id || entry.actor_type}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {entry.actor_type}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                      {entry.action}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {entry.entity_type ? (
                      <Typography variant="body2">
                        {entry.entity_type}
                        {entry.entity_id ? ` · ${entry.entity_id.slice(0, 8)}` : ""}
                      </Typography>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusChip status={entry.status} />
                  </TableCell>
                  <TableCell>{entry.source_ip || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
          }
        />
      </Paper>

      {/* Detail dialog */}
      <Dialog
        open={selected !== null}
        onClose={() => setSelected(null)}
        maxWidth="sm"
        fullWidth
        aria-label="Audit entry detail"
      >
        <DialogTitle sx={{ fontFamily: "monospace", fontSize: "1rem" }}>
          {selected?.action}
        </DialogTitle>
        <DialogContent>
          {selected && (
            <>
              <Typography variant="body2" paragraph>
                {formatDateTime(selected.created_at)} ·{" "}
                {selected.actor_username || selected.actor_id || selected.actor_type}
                {selected.source_ip ? ` · ${selected.source_ip}` : ""}
              </Typography>
              {selected.error_message && (
                <Typography variant="body2" color="error" paragraph>
                  {selected.error_message}
                </Typography>
              )}
              <StateBlock label="Detail" value={selected.detail} />
              <StateBlock label="Before" value={selected.before_state} />
              <StateBlock label="After" value={selected.after_state} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
